import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";

import { translate } from "app/filters/i18nFilter";
import { MAX_NAME_LENGTH } from "app/pages/activation/components/RankWidget/config";
import { InfoBanner } from "app/pages/activation/components/RankWidget/CreationModal/Components/InfoBanner/InfoBanner";
import { creationModalKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { createCampaignName } from "app/pages/activation/components/RankWidget/helpers/createCampainName";
import { nameValidation } from "app/pages/activation/components/RankWidget/helpers/nameValidation";
import { modalsTexts } from "app/pages/activation/components/RankWidget/texts";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";

import * as SC from "../../styles";

type NameStepSchema = {
    projectName?: string;
    setFormData: Dispatch<SetStateAction<RTFormData>>;
    existingCampaignsNames?: string[];
};

export const NameStep = ({ projectName, setFormData, existingCampaignsNames = [] }: NameStepSchema) => {
    const [inputValue, setInputValue] = useState<string>(createCampaignName(projectName));
    const [inputError, setInputError] = useState<string | null>(null);

    useEffect(() => {
        setFormData((prevData) => ({ ...prevData, name: inputValue }));
    }, [inputValue, setFormData]);

    useEffect(() => {
        setFormData((prevData) => ({ ...prevData, hasError: !!inputError }));
    }, [inputError, setFormData]);

    useEffect(() => {
        const name = createCampaignName(projectName);
        const error = nameValidation(name, existingCampaignsNames);

        setInputValue(name);
        setInputError(error);
    }, [existingCampaignsNames, projectName]);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const error = nameValidation(value, existingCampaignsNames);

        setInputError(error);
        setInputValue(value);
    };

    return (
        <>
            <SC.Title data-automation={creationModalKeys.nameStep.title}>
                {translate(modalsTexts.nameStepTitle)}
            </SC.Title>

            <SC.Content
                data-automation={creationModalKeys.nameStep.description}
                step="name"
            >
                <p>{translate(modalsTexts.nameStepDescription)}</p>

                <SC.InputContainer
                    isError={!!inputError}
                    data-automation={creationModalKeys.nameStep.input}
                >
                    <SC.InputLabel data-automation={creationModalKeys.nameStep.label}>
                        {translate(modalsTexts.nameInputLabel)}
                    </SC.InputLabel>

                    <SC.StyledInput
                        value={inputValue}
                        onChange={onChange}
                        placeholder={translate(modalsTexts.nameInputPlaceholder)}
                        isError={!!inputError}
                        errorMessage={inputError}
                        maxLength={MAX_NAME_LENGTH}
                    />
                </SC.InputContainer>

                <InfoBanner />
            </SC.Content>
        </>
    );
};
